import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';

/* === Iconos inline: ASC / DESC (barras de distinta longitud) === */
const AscIcon = (props) => (
  <svg
    width='18'
    height='13'
    viewBox='0 0 18 13'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
    aria-hidden='true'
    {...props}>
    {/* corto / medio / largo  → Ascendente */}
    <path d='M0 12.63H6V10.63H0V12.63Z' fill='currentColor' />
    <path d='M0 7.63H12V5.63H0V7.63Z' fill='currentColor' />
    <path d='M0 2.63H18V0.63H0V2.63Z' fill='currentColor' />
  </svg>
);
const DescIcon = (props) => (
  <svg
    width='18'
    height='13'
    viewBox='0 0 18 13'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
    aria-hidden='true'
    {...props}>
    {/* largo / medio / corto → Descendente */}
    <path d='M0 12.63H18V10.63H0V12.63Z' fill='currentColor' />
    <path d='M0 7.63H12V5.63H0V7.63Z' fill='currentColor' />
    <path d='M0 2.63H6V0.63H0V2.63Z' fill='currentColor' />
  </svg>
);

const styles = () => ({
  root: {
    minWidth: 143,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'nowrap',
    '@media (max-width: 960px)': {
      width: '100%',
      flexDirection: 'column',
      alignItems: 'stretch',
      gap: 6,
    },
  },

  filter: {
    width: 'auto',
    minWidth: 90,
    height: 56,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    padding: '10px 12px 10px 14px',
    background: '#F1F9FF',
    border: '1px solid #C0D2E5',
    position: 'relative',
    cursor: 'pointer',
    '@media (max-width: 960px)': { width: '100%', minWidth: 0, height: 52, padding: '10px 12px' },
    '@media (max-width: 375px)': { height: 48 },
  },

  valueText: {
    flex: 1,
    minWidth: 0,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    color: '#181D27',
    fontFamily: 'var(--font-sans)',
    fontSize: 16,
    lineHeight: '24px',
    fontWeight: 400,
  },

  /* Icono con animación */
  iconWrap: {
    position: 'relative',
    width: 18,
    height: 13,
    flex: '0 0 auto',
    display: 'inline-grid',
    placeItems: 'center',
    padding: 0,
    margin: 0,
    background: 'transparent', // ← quita el fill nativo
    border: 0, // ← quita el borde nativo
    lineHeight: 0,
    color: '#0C70E8',
    cursor: 'pointer',
    appearance: 'none',
    WebkitAppearance: 'none',
    MozAppearance: 'none',
    outline: 'none',
    borderRadius: 4,
    // foco accesible
    '&:focus-visible': {
      outline: '2px solid rgba(12,112,232,.6)',
      outlineOffset: 2,
    },
    // Firefox quita el “inner focus”
    '&::-moz-focus-inner': { border: 0 },
  },
  iconLayer: {
    position: 'absolute',
    inset: 0,
    color: 'currentColor',
    transition: 'opacity .22s ease, transform .22s ease',
    willChange: 'opacity, transform',
    pointerEvents: 'none', // ← evita capturar eventos el SVG
  },
  hidden: { opacity: 0, transform: 'translateY(4px) scale(.96)' },
  visible: { opacity: 1, transform: 'translateY(0) scale(1)' },

  /* dropdown */
  menu: {
    position: 'absolute',
    top: 'calc(100% + 6px)',
    left: -1,
    width: 'calc(100% + 2px)',
    background: '#fff',
    border: '1px solid #C0D2E5',
    boxShadow: '16px 16px 32px rgba(23,65,151,0.12)',
    padding: 0,
    display: 'none',
    zIndex: 20,
  },
  menuOpen: { display: 'block' },

  option: {
    padding: '12px 14px',
    fontFamily: 'var(--font-sans)',
    fontSize: 16,
    lineHeight: '24px',
    color: '#181D27',
    userSelect: 'none',
  },
  optionHover: { background: '#E9F3FF' },
  optionSelected: {
    background: '#E3F2FF',
    color: '#0C70E8',
    fontWeight: 500,
  },
});

function PbFilter(props) {
  const { classes, filters, onSelected, orderByAsc } = props;

  const [optionSelected, setOptionSelected] = useState('Title');
  const [order, setOrder] = useState(true); // true = asc
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);

  /* Mantén el icono sincronizado si el padre cambia el orden externamente */
  useEffect(() => {
    if (typeof orderByAsc === 'boolean') setOrder(orderByAsc);
  }, [orderByAsc]);

  /* Notifica cambios al padre */
  useEffect(() => {
    if (onSelected) onSelected(optionSelected, order);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [optionSelected, order]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (!boxRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const choose = (val) => {
    setOptionSelected(val);
    setOpen(false);
  };
  const onKey = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setOpen((v) => !v);
    }
    if (e.key === 'Escape') setOpen(false);
  };

  const current =
    (filters ?? []).find((f) => f.Value === optionSelected)?.Description ?? optionSelected;

  return (
    <div className={`${classes.root} filter-select`}>
      <span
        style={{
          color: '#002857',
          fontFamily: 'var(--font-sans)',
          fontSize: 13,
          lineHeight: '100%',
          letterSpacing: '-0.02em',
          fontWeight: 400,
          whiteSpace: 'nowrap',
          flex: '0 0 auto',
        }}>
        Sort by
      </span>

      <div
        ref={boxRef}
        className={classes.filter}
        role='button'
        tabIndex={0}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={onKey}
        aria-haspopup='listbox'
        aria-expanded={open}>
        <span className={classes.valueText}>{current}</span>

        {/* Botón de orden con animación y cambio de icono */}
        <button
          type='button'
          className={classes.iconWrap}
          onClick={(e) => {
            e.stopPropagation();
            setOrder((o) => !o);
          }}
          aria-label={order ? 'Change to descending' : 'Change to ascending'}
          aria-pressed={!order}
          title={order ? 'Ascending' : 'Descending'}>
          <DescIcon
            className={`${classes.iconLayer} ${order ? classes.visible : classes.hidden}`}
          />
          <AscIcon className={`${classes.iconLayer} ${order ? classes.hidden : classes.visible}`} />
        </button>

        <div className={`${classes.menu} ${open ? classes.menuOpen : ''}`} role='listbox'>
          {(filters ?? []).map((val) => (
            <div
              key={val.Value}
              role='option'
              aria-selected={val.Value === optionSelected}
              className={[
                classes.option,
                val.Value === optionSelected ? classes.optionSelected : '',
              ].join(' ')}
              onMouseEnter={(e) => e.currentTarget.classList.add(classes.optionHover)}
              onMouseLeave={(e) => e.currentTarget.classList.remove(classes.optionHover)}
              onClick={(e) => {
                e.stopPropagation();
                choose(val.Value);
              }}>
              {val.Description}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

PbFilter.propTypes = {
  filters: PropTypes.array,
  onSelected: PropTypes.func,
  orderByAsc: PropTypes.bool,
};
PbFilter.defaultProps = {
  filters: [
    { Description: 'Duration', Value: 'Duration', Selected: false },
    { Description: 'Upload Time', Value: 'UploadTime', Selected: true },
    { Description: 'Title', Value: 'Title', Selected: false },
  ],
  orderByAsc: true,
};

export default withStyles(styles)(PbFilter);
